import { useEffect } from 'react'

import type { NextPage } from 'next'
import type { AppProps } from 'next/app'
import NextImage from 'next/image'

import { BrowserRouter } from 'react-router-dom'

import { parseLocale } from '@b2b/shared'
import { I18nProvider } from '@twisto/base/i18n-provider'
import { regionObjects } from '@twisto/base/region'
import { Browser } from '@twistopayments/design-system/components/atoms/browser'
import { CssBaseline } from '@twistopayments/design-system/components/atoms/css-baseline'
import { ErrorModalProvider } from '@twistopayments/design-system/components/molecules/error-boundary'
import type {
  ImageComponent as DSImageComponent,
  LinkComponent as DSLinkComponent,
} from '@twistopayments/design-system/base'
import { DesignSystemProvider } from '@twistopayments/design-system/base'

import { NextLinkAdapter } from '../components/next-link-adapter'
import {
  RestApiProvider,
  useCustomerSession,
} from '../components/rest-api-provider/rest-api-provider'

// Base styles for the react-day-picker calendar used by the DS <DateRange>.
// The DS ships the component without importing its CSS (to keep it out of the
// published bundle), so the host app registers it once here.
import 'react-day-picker/dist/style.css'

type AppPropsWithLayout = AppProps & {
  Component: NextPage
}

const CustomerRoutes = ({
  Component,
  pageProps,
  router,
}: AppPropsWithLayout) => {
  const { status } = useCustomerSession()
  const isAuthPath = router.asPath.startsWith('/auth')

  useEffect(() => {
    if (!isAuthPath && status === 'anonymous') {
      router.replace('/auth/login/')
    }
  }, [isAuthPath, router, status])

  if (!isAuthPath && status !== 'authenticated') {
    return null
  }

  const getLayout = Component.getLayout ?? (({ children }) => children)

  return (
    <BrowserRouter>
      {getLayout({ children: <Component {...pageProps} /> })}
    </BrowserRouter>
  )
}

export default function App({
  Component,
  pageProps,
  router,
}: AppPropsWithLayout) {
  const { region, lang } = parseLocale(router.locale)
  const { currencyCode } = regionObjects[region]

  useEffect(() => {
    window.document.documentElement.setAttribute('data-theme', 'light')
  }, [])

  useEffect(() => {
    window.REGION = region
    window.LANG = lang
    window.BASEPATH = process.env.BASEPATH ?? ''
  }, [region, lang])

  return (
    <DesignSystemProvider
      brand="b2b"
      currencyCode={currencyCode}
      themeMode="light"
      ImageComponent={NextImage as unknown as DSImageComponent}
      LinkComponent={NextLinkAdapter as unknown as DSLinkComponent}
    >
      <I18nProvider language={lang} translationBundle="customer">
        <CssBaseline fontScaling={false} />
        <Browser>
          <ErrorModalProvider>
            <RestApiProvider>
              <CustomerRoutes
                Component={Component}
                pageProps={pageProps}
                router={router}
              />
            </RestApiProvider>
          </ErrorModalProvider>
        </Browser>
      </I18nProvider>
    </DesignSystemProvider>
  )
}
